// CartDrawer — persistent cart surface. Opens from the right, closes via
// backdrop / ESC / × / Checkout.
//
// Spec: PROMPT 2.5 §1-7.
//   - H1: no localStorage cart. Every open re-fetches /api/cart/me (handled
//     by CartProvider.openDrawer → load()).
//   - H2/R8: no timer fallback anywhere.
//   - H3: images from cdn.shopify.com only.
//   - H4: no external fonts.
//   - H7: no <dialog>, custom fixed-position surface.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCart } from '../../state/cart';
import { useAccount } from '../../state/account';
import { getEta } from '../../data/preorderEta';
import {
        isWarrantyEligible,
        isWarrantyLine,
        warrantyLineFor,
        fetchWarrantyVariantId,
} from '../../lib/warrantyAddon';
import { isFreebieLine, declineFreebie } from '../../lib/freebies';
import { FreebieBar, FreebieRow } from './FreebieBar';
import CrossSell from './CrossSell';
import './CartDrawer.css';

const FMT = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
});
function money(amount) {
        const n = Number(amount);
        return Number.isFinite(n) ? FMT.format(n) : '—';
}

function variantLabel(line) {
        const opts = (line?.merchandise?.selectedOptions) || [];
        return opts.map((o) => o.value).filter(Boolean).join(' / ');
}
function isPreorderLine(line) {
        const attrs = line?.attributes || [];
        return attrs.some((a) => a && a.key === '_intent' && a.value === 'preorder');
}
function productHandle(line) {
        return line?.merchandise?.product?.handle || null;
}
function linePriceEach(line) {
        return Number(line?.merchandise?.price?.amount || 0);
}
function lineSubtotal(line) {
        return linePriceEach(line) * (line?.quantity || 0);
}

// ---------- inline SVG glyphs (spec: no CDN) ----------
function CloseGlyph({ size = 18 }) {
        return (
                <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
        );
}
function TrashGlyph({ size = 16 }) {
        return (
                <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
        );
}
function BagGlyph({ size = 44 }) {
        return (
                <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
                        <path d="M12 16h24l-2 26H14L12 16z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        <path d="M18 16v-3a6 6 0 1112 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
        );
}

// ---------- component ----------
export default function CartDrawer() {
        const {
                drawerOpen, closeDrawer, lines, totalQuantity, checkoutUrl, loading,
                updateQuantity, removeLine, removeLineById, addWarranty, checkoutAsGuest,
        } = useCart();
        const { status } = useAccount();
        const panelRef = useRef(null);
        const [guestBusy, setGuestBusy] = useState(false);
        const [guestError, setGuestError] = useState('');
        const [warrantyVariantId, setWarrantyVariantId] = useState(null);

        const freebieLines = (lines || []).filter((l) => isFreebieLine(l));
        const regularLines = (lines || []).filter((l) => !isWarrantyLine(l) && !isFreebieLine(l));
        const hasEligible = regularLines.some((l) =>
                isWarrantyEligible(l?.merchandise?.product?.productType));

        // Manual gift removal: mark declined (no auto re-add this session),
        // then drop the line server-side.
        const onRemoveFreebie = useCallback(async (line) => {
                const h = line?.merchandise?.product?.handle;
                if (h) declineFreebie(h);
                await removeLineById(line.id);
        }, [removeLineById]);

        // Resolve the extended-warranty product variant once the drawer is
        // open and an eligible line exists. Hidden (404 / draft) → no button.
        useEffect(() => {
                if (!drawerOpen || !hasEligible || warrantyVariantId) return undefined;
                let alive = true;
                fetchWarrantyVariantId().then((v) => {
                        if (alive && v) setWarrantyVariantId(v);
                });
                return () => { alive = false; };
        }, [drawerOpen, hasEligible, warrantyVariantId]);

        const onAddWarranty = useCallback(async (parentLineId) => {
                if (!warrantyVariantId) return { ok: false };
                return addWarranty(warrantyVariantId, parentLineId);
        }, [warrantyVariantId, addWarranty]);

        // ESC to close + focus trap while open
        useEffect(() => {
                if (!drawerOpen) return undefined;
                const onKey = (e) => {
                        if (e.key === 'Escape') {
                                e.preventDefault();
                                closeDrawer();
                                return;
                        }
                        if (e.key !== 'Tab') return;
                        // Focus trap
                        const panel = panelRef.current;
                        if (!panel) return;
                        const focusables = panel.querySelectorAll(
                                'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
                        );
                        if (focusables.length === 0) return;
                        const first = focusables[0];
                        const last = focusables[focusables.length - 1];
                        if (e.shiftKey && document.activeElement === first) {
                                e.preventDefault();
                                last.focus();
                        } else if (!e.shiftKey && document.activeElement === last) {
                                e.preventDefault();
                                first.focus();
                        }
                };
                document.addEventListener('keydown', onKey);
                // Focus the close button on open
                requestAnimationFrame(() => {
                        const btn = panelRef.current?.querySelector('[data-testid="cart-drawer-close"]');
                        if (btn) btn.focus();
                });
                return () => document.removeEventListener('keydown', onKey);
        }, [drawerOpen, closeDrawer]);

        // Body scroll lock while open
        useEffect(() => {
                if (!drawerOpen) return undefined;
                const prev = document.body.style.overflow;
                document.body.style.overflow = 'hidden';
                return () => {
                        document.body.style.overflow = prev;
                };
        }, [drawerOpen]);

        const total = (lines || []).reduce((acc, l) => acc + lineSubtotal(l), 0);
        const hasLines = regularLines.length > 0;

        const onCheckout = () => {
                if (!hasLines) return;
                if (status !== 'authed') {
                        window.location.href = '/account/login?return_path=/cart/checkout';
                        return;
                }
                if (checkoutUrl) {
                        window.location.href = checkoutUrl;
                }
        };

        // BUG 5 — Guest checkout: detach customerAccessToken (if any) from
        // the cart and send the user straight to the Shopify-hosted
        // checkoutUrl. Shopify's "Accounts optional" setting on the Online
        // Store ultimately decides whether the checkout UI shows the guest
        // email path.
        const guestAvailable = hasLines;
        const onGuestCheckout = async () => {
                if (!guestAvailable || guestBusy) return;
                setGuestError('');
                setGuestBusy(true);
                const res = await checkoutAsGuest();
                setGuestBusy(false);
                if (res && res.ok && res.checkoutUrl) {
                        window.location.href = res.checkoutUrl;
                        return;
                }
                // Last-resort: use the cart's existing checkoutUrl even if the
                // buyerIdentity clear failed — Shopify will handle guest-vs-
                // account at the checkout step itself.
                if (checkoutUrl) {
                        window.location.href = checkoutUrl;
                        return;
                }
                setGuestError('Checkout unavailable — try again.');
        };

        return (
                <>
                        {/* Backdrop — always mounted, uses opacity+pointer-events for close transition */}
                        <div
                                data-testid="cart-drawer-backdrop"
                                className={'cart-drawer-backdrop ' + (drawerOpen ? '' : 'is-closed')}
                                onClick={closeDrawer}
                                aria-hidden={!drawerOpen}
                        />
                        <aside
                                ref={panelRef}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Your cart"
                                data-testid="cart-drawer-panel"
                                className={'cart-drawer-panel ' + (drawerOpen ? '' : 'is-closed')}
                        >
                                {/* Header */}
                                <header className="cart-drawer-header flex items-center justify-between px-5 py-4 border-b border-white/10">
                                        <p
                                                data-testid="cart-drawer-title"
                                                className="font-mono text-[11px] tracking-[0.12em] uppercase text-white/85"
                                        >
                                                Your Cart{typeof totalQuantity === 'number' && totalQuantity > 0 ? ` · ${totalQuantity}` : ''}
                                        </p>
                                        <button
                                                type="button"
                                                data-testid="cart-drawer-close"
                                                aria-label="Close cart"
                                                onClick={closeDrawer}
                                                className="text-white/70 hover:text-[#FF9F1C] transition-colors p-1"
                                        >
                                                <CloseGlyph />
                                        </button>
                                </header>

                                {/* Body */}
                                <div className="cart-drawer-body px-5 py-5">
                                        {loading && !hasLines ? (
                                                <p data-testid="cart-drawer-loading" className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/50">
                                                        Loading…
                                                </p>
                                        ) : hasLines ? (
                                                <ul data-testid="cart-drawer-lines" className="space-y-6">
                                                        {regularLines.map((line) => (
                                                                <CartLineRow
                                                                        key={line.id}
                                                                        line={line}
                                                                        allLines={lines}
                                                                        warrantyAvailable={Boolean(warrantyVariantId)}
                                                                        onAddWarranty={onAddWarranty}
                                                                        onRemoveWarranty={removeLineById}
                                                                        onUpdate={updateQuantity}
                                                                        onRemove={removeLine}
                                                                />
                                                        ))}
                                                        {freebieLines.map((line) => (
                                                                <FreebieRow key={line.id} line={line} onRemove={onRemoveFreebie} />
                                                        ))}
                                                </ul>
                                        ) : (
                                                <EmptyState onClose={closeDrawer} />
                                        )}
                                        {hasLines && <CrossSell lines={lines} />}
                                </div>

                                {/* Footer */}
                                <footer className="cart-drawer-footer px-5 py-5 border-t border-white/10 space-y-3">
                                        {hasLines && <FreebieBar lines={lines} />}
                                        {regularLines.some(isPreorderLine) && (
                                                <p
                                                        data-testid="cart-preorder-policy-note"
                                                        className="font-mono text-[9px] tracking-[0.12em] uppercase text-white/45 leading-relaxed"
                                                >
                                                        Contains pre-order items ·{" "}
                                                        <a href="/preorder-policy" className="text-[#FF9F1C] hover:underline">
                                                                Read the Pre-Order Policy ➔
                                                        </a>
                                                </p>
                                        )}
                                        <div className="flex items-center justify-between">
                                                <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">Total</span>
                                                <span
                                                        data-testid="cart-drawer-total"
                                                        className="font-mono text-[15px] text-white tabular-nums"
                                                >
                                                        {money(total)}
                                                </span>
                                        </div>
                                        <button
                                                type="button"
                                                data-testid="cart-drawer-checkout"
                                                onClick={onCheckout}
                                                disabled={!hasLines}
                                                className={
                                                        'w-full font-mono text-[12px] tracking-[0.2em] uppercase py-3.5 border transition-colors ' +
                                                        (hasLines
                                                                ? 'border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] hover:bg-transparent hover:text-[#FF9F1C]'
                                                                : 'border-white/15 text-white/40 cursor-not-allowed')
                                                }
                                        >
                                                {status === 'authed' ? 'Checkout →' : 'Continue with Shopify →'}
                                        </button>
                                        {/* BUG 5 — Guest checkout. Enabled only when cart has lines. */}
                                        <button
                                                type="button"
                                                data-testid="cart-drawer-guest-checkout"
                                                onClick={onGuestCheckout}
                                                disabled={!guestAvailable || guestBusy}
                                                className={
                                                        'w-full font-mono text-[12px] tracking-[0.2em] uppercase py-3.5 border transition-colors ' +
                                                        (guestAvailable
                                                                ? 'border-white/40 text-white hover:border-[#FF9F1C] hover:text-[#FF9F1C]'
                                                                : 'border-white/15 text-white/40 cursor-not-allowed')
                                                }
                                        >
                                                {guestBusy ? 'Opening…' : 'Continue as guest →'}
                                        </button>
                                        {guestError && (
                                                <p
                                                        data-testid="cart-drawer-guest-error"
                                                        className="font-mono text-[10px] tracking-[0.14em] uppercase text-red-400"
                                                >
                                                        {guestError}
                                                </p>
                                        )}
                                        <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/40 leading-relaxed">
                                                100% Upfront via Razorpay · No charge until dispatch · Ships within arrival window
                                        </p>
                                </footer>
                        </aside>
                </>
        );
}

function EmptyState({ onClose }) {
        return (
                <div data-testid="cart-drawer-empty" className="flex flex-col items-center text-center pt-8 pb-4">
                        <span className="text-white/25 mb-5"><BagGlyph /></span>
                        <p className="font-mono text-[12px] tracking-[0.18em] uppercase text-white/70">
                                Your cart is empty
                        </p>
                        <a
                                href="/"
                                onClick={onClose}
                                data-testid="cart-drawer-bestsellers"
                                className="mt-5 font-mono text-[11px] tracking-[0.16em] uppercase text-[#FF9F1C] hover:underline"
                        >
                                Best Sellers →
                        </a>
                </div>
        );
}

function CartLineRow({ line, allLines = [], warrantyAvailable = false, onAddWarranty, onRemoveWarranty, onUpdate, onRemove }) {
        const [busy, setBusy] = useState(false);
        const [removing, setRemoving] = useState(false);
        const [warrantyBusy, setWarrantyBusy] = useState(false);
        const [rowError, setRowError] = useState(null);

        const variantId = line?.merchandise?.id;
        const attrs = line?.attributes || [];
        const qty = line?.quantity || 0;
        const preorder = isPreorderLine(line);
        const handle = productHandle(line);
        const eta = preorder ? getEta(handle, { isPreorder: true }) : null;
        const priceEach = linePriceEach(line);
        const subtotal = money(priceEach * qty);

        const eligibleForWarranty = isWarrantyEligible(line?.merchandise?.product?.productType);
        const existingWarranty = warrantyLineFor(line.id, allLines);

        const doSet = async (newQty) => {
                if (busy || removing) return;
                setRowError(null);
                setBusy(true);
                const res = await onUpdate(variantId, newQty, attrs);
                setBusy(false);
                if (!res.ok) setRowError('Quantity update failed — try again.');
        };
        const doRemove = async () => {
                if (busy || removing) return;
                setRowError(null);
                setRemoving(true);
                const res = await onRemove(variantId, attrs);
                if (!res.ok) {
                        setRemoving(false);
                        setRowError('Quantity update failed — try again.');
                        return;
                }
                // Cascade: drop orphaned warranty add-ons tied to this line.
                const orphans = (res.lines || []).filter(
                        (l) => warrantyLineFor(line.id, [l]));
                for (const o of orphans) {
                        // eslint-disable-next-line no-await-in-loop
                        await onRemoveWarranty(o.id);
                }
        };
        const doAddWarranty = async () => {
                if (warrantyBusy || existingWarranty) return;
                setRowError(null);
                setWarrantyBusy(true);
                const res = await onAddWarranty(line.id);
                setWarrantyBusy(false);
                if (!res || !res.ok) setRowError('Could not add warranty — try again.');
        };

        return (
                <li data-testid="cart-drawer-line" className="flex gap-4">
                        {/* Thumbnail (cdn.shopify.com per H3) */}
                        <div className="shrink-0 w-[76px] h-[76px] border border-white/10 bg-[#111] flex items-center justify-center overflow-hidden">
                                {line?.merchandise?.image?.url ? (
                                        <img
                                                src={line.merchandise.image.url}
                                                alt={line.merchandise.image.altText || line.merchandise.product?.title || 'variant'}
                                                loading="lazy"
                                                className="max-w-full max-h-full object-contain p-1.5"
                                        />
                                ) : (
                                        <span className="text-white/25 font-display text-[24px]">
                                                {(line?.merchandise?.product?.title || '?').slice(0, 1)}
                                        </span>
                                )}
                        </div>

                        {/* Body */}
                        <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                                <h3
                                                        data-testid="cart-drawer-line-title"
                                                        className="font-sans text-[14px] font-semibold text-white leading-tight truncate"
                                                >
                                                        {line?.merchandise?.product?.title || line?.merchandise?.title || 'Item'}
                                                </h3>
                                                <p
                                                        data-testid="cart-drawer-line-variant"
                                                        className="mt-1 font-mono text-[10px] tracking-[0.14em] uppercase text-white/50 truncate"
                                                >
                                                        {variantLabel(line) || line?.merchandise?.title || ''}
                                                </p>
                                                {preorder && (
                                                        <p
                                                                data-testid="cart-drawer-line-preorder"
                                                                className="mt-2 inline-block font-mono text-[9px] tracking-[0.14em] uppercase text-[#FF9F1C] border border-[#FF9F1C]/60 px-1.5 py-0.5"
                                                        >
                                                                Pre-order{eta ? ` · Dispatches ${fmtDay(eta.from)} - ${fmtDay(eta.to)}` : ''}
                                                        </p>
                                                )}
                                        </div>
                                        <button
                                                type="button"
                                                data-testid="cart-drawer-line-remove"
                                                aria-label={`Remove ${line?.merchandise?.product?.title || 'item'} from cart`}
                                                onClick={doRemove}
                                                disabled={busy || removing}
                                                className="text-white/40 hover:text-[#FF9F1C] transition-colors p-1"
                                        >
                                                <TrashGlyph />
                                        </button>
                                </div>

                                <div className="mt-3 flex items-center justify-between gap-3">
                                        <div className="inline-flex items-center border border-white/15 divide-x divide-white/10">
                                                <button
                                                        type="button"
                                                        data-testid="cart-drawer-line-qty-decrement"
                                                        aria-label="Decrease quantity"
                                                        onClick={() => doSet(qty - 1)}
                                                        disabled={busy || removing || qty <= 1}
                                                        className="px-3 py-1.5 font-mono text-[13px] text-white/80 hover:text-[#FF9F1C] disabled:text-white/25 disabled:cursor-not-allowed transition-colors"
                                                >
                                                        −
                                                </button>
                                                <span
                                                        data-testid="cart-drawer-line-qty"
                                                        className="px-3.5 py-1.5 font-mono text-[13px] text-white tabular-nums min-w-[40px] text-center"
                                                >
                                                        {removing ? '…' : qty}
                                                </span>
                                                <button
                                                        type="button"
                                                        data-testid="cart-drawer-line-qty-increment"
                                                        aria-label="Increase quantity"
                                                        onClick={() => doSet(qty + 1)}
                                                        disabled={busy || removing || qty >= 5}
                                                        className="px-3 py-1.5 font-mono text-[13px] text-white/80 hover:text-[#FF9F1C] disabled:text-white/25 disabled:cursor-not-allowed transition-colors"
                                                >
                                                        +
                                                </button>
                                        </div>
                                        <span
                                                data-testid="cart-drawer-line-subtotal"
                                                className="font-mono text-[13px] text-white tabular-nums"
                                        >
                                                {subtotal}
                                        </span>
                                </div>

                                {/* Extended-warranty add-on (BUG: warranty upsell)
                                     Only for eligible product types, and only once the
                                     hidden warranty product's variant has resolved. */}
                                {eligibleForWarranty && warrantyAvailable && (
                                        <div className="mt-3">
                                                {existingWarranty ? (
                                                        <>
                                                                <div
                                                                        data-testid="cart-drawer-warranty-line"
                                                                        className="flex items-center justify-between gap-3 border border-white/10 bg-white/[0.03] px-3 py-2"
                                                                >
                                                                        <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-white/60">
                                                                                Extended Warranty (+6 months)
                                                                        </span>
                                                                        <span className="flex items-center gap-2">
                                                                                <span
                                                                                        data-testid="cart-drawer-warranty-price"
                                                                                        className="font-mono text-[11px] text-white tabular-nums"
                                                                                >
                                                                                        {money(linePriceEach(existingWarranty) * (existingWarranty.quantity || 1))}
                                                                                </span>
                                                                                <button
                                                                                        type="button"
                                                                                        data-testid="cart-drawer-warranty-remove"
                                                                                        aria-label="Remove extended warranty"
                                                                                        onClick={() => onRemoveWarranty(existingWarranty.id)}
                                                                                        className="text-white/40 hover:text-[#FF9F1C] transition-colors p-1"
                                                                                >
                                                                                        <TrashGlyph size={14} />
                                                                                </button>
                                                                        </span>
                                                                </div>
                                                                <button
                                                                        type="button"
                                                                        disabled
                                                                        data-testid="cart-drawer-add-warranty"
                                                                        className="mt-2 w-full border border-white/15 text-white/40 font-mono text-[10px] tracking-[0.14em] uppercase px-3 py-2.5 cursor-not-allowed"
                                                                >
                                                                        Extended warranty already added
                                                                </button>
                                                        </>
                                                ) : (
                                                        <button
                                                                type="button"
                                                                onClick={doAddWarranty}
                                                                disabled={warrantyBusy}
                                                                data-testid="cart-drawer-add-warranty"
                                                                className="w-full border border-dashed border-[#FF9F1C]/50 text-[#FF9F1C] hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/10 font-mono text-[10px] tracking-[0.14em] uppercase px-3 py-2.5 transition-colors disabled:opacity-50"
                                                        >
                                                                {warrantyBusy ? 'Adding…' : 'Add 6-month extended warranty (2 yrs total) — ₹1,000'}
                                                        </button>
                                                )}
                                        </div>
                                )}

                                {rowError && (
                                        <p
                                                data-testid="cart-drawer-line-error"
                                                className="mt-2 font-mono text-[10px] tracking-[0.14em] uppercase text-red-400"
                                        >
                                                {rowError}
                                        </p>
                                )}
                        </div>
                </li>
        );
}

function fmtDay(d) {
        const dt = d instanceof Date ? d : new Date(d);
        if (isNaN(dt.getTime())) return '';
        const day = dt.getDate();
        const mon = dt.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        return `${day} ${mon}`;
}
